import { convert24To12Hour } from '../utils'
import { InactiveTime } from './useInactiveTimesViewModel'

const daysOfWeek = [
  { short: 'Sun', full: 'Sunday' },
  { short: 'Mon', full: 'Monday' },
  { short: 'Tue', full: 'Tuesday' },
  { short: 'Wed', full: 'Wednesday' },
  { short: 'Thu', full: 'Thursday' },
  { short: 'Fri', full: 'Friday' },
  { short: 'Sat', full: 'Saturday' },
]

interface UseInactiveTimeRowViewModelProps {
  value: InactiveTime
  onRemove: (id: string) => void
  onChange: (
    id: string,
    patch: Partial<Pick<InactiveTime, 'startTime' | 'endTime' | 'days'>>,
  ) => void
  onToggleDay: (id: string, dayIndex: number) => void
}

export default function useInactiveTimeRowViewModel({
  value,
  onRemove,
  onChange,
  onToggleDay,
}: UseInactiveTimeRowViewModelProps) {
  const { id, startTime, endTime, days } = value

  // Check if end time crosses over to next day (end time is before start time)
  const crossesOverMidnight = (() => {
    const [startHour, startMinute] = startTime.split(':').map(Number)
    const [endHour, endMinute] = endTime.split(':').map(Number)
    const startMinutes = startHour * 60 + startMinute
    const endMinutes = endHour * 60 + endMinute
    return endMinutes < startMinutes
  })()

  const getDaysDisplayText = () => {
    if (days.length === 0) return 'Select day(s) above'

    // Check for weekends (Saturday = 6, Sunday = 0)
    const hasWeekends = days.includes(0) && days.includes(6)
    const weekendDays = days.filter((day) => day === 0 || day === 6)

    // Check for weekdays (Monday = 1, Tuesday = 2, ..., Friday = 5)
    const weekdayIndices = [1, 2, 3, 4, 5]
    const hasAllWeekdays = weekdayIndices.every((day) => days.includes(day))

    if (hasWeekends && weekendDays.length === 2 && days.length === 2) {
      return 'Weekends'
    }

    if (hasAllWeekdays && days.length === 5) {
      return 'Weekdays'
    }

    if (days.length === 7) {
      return 'Every day'
    }

    // Return comma-separated list of day shorthand names
    return days
      .sort((a, b) => a - b)
      .map((dayIndex) => daysOfWeek[dayIndex].short)
      .join(', ')
  }

  const getEndsText = () => {
    if (days.length === 0) {
      return ''
    }

    const sortedDays = days.sort((a, b) => a - b)
    const lastDayIndex = sortedDays[sortedDays.length - 1]

    // If crossing over midnight, show next day after the last scheduled day
    const endsOnDayIndex = crossesOverMidnight
      ? (lastDayIndex + 1) % 7 // Wrap around (Sunday = 0, so Saturday + 1 = 0)
      : lastDayIndex

    const endsDayName = daysOfWeek[endsOnDayIndex]?.full
    const endTime12Hour = convert24To12Hour(endTime)

    return `(Ends ${endsDayName} at ${endTime12Hour})`
  }

  const handleRemove = () => {
    onRemove(id)
  }

  const handleStartTimeChange = ({
    hour,
    minute,
  }: {
    hour: number
    minute: number
  }) => {
    onChange(id, {
      startTime: `${String(hour).padStart(2, '0')}:${String(minute).padStart(
        2,
        '0',
      )}`,
    })
  }

  const handleEndTimeChange = ({
    hour,
    minute,
  }: {
    hour: number
    minute: number
  }) => {
    onChange(id, {
      endTime: `${String(hour).padStart(2, '0')}:${String(minute).padStart(
        2,
        '0',
      )}`,
    })
  }

  const handleToggleDay = (dayIndex: number) => {
    onToggleDay(id, dayIndex)
  }

  return {
    id,
    startTime,
    endTime,
    days,
    daysOfWeek,
    crossesOverMidnight,
    daysDisplayText: getDaysDisplayText(),
    endsText: getEndsText(),
    handleRemove,
    handleStartTimeChange,
    handleEndTimeChange,
    handleToggleDay,
  }
}
